import { useState, useEffect } from "react";
import {
  Button,
  Nav,
  Container,
  Row,
  Col,
  Table as BTable,
  Spinner,
} from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { TableCell } from "@/components/Elements/TableCell";
import { managerApi } from "@/api";
import "./Manager.scss";
import Tabs from "@/components/Elements/tabs";
import { internTaskOptions, timePeriods } from "@/constants/taskTypes";

const getFieldName = (taskType) => {
  const fields = {
    tracks: "track",
    super_tracks: "super_track",
  };
  return "can_solve_" + fields[taskType];
};

const Manager = () => {
  const [selectedPermissionType, setSelectedPermissionType] = useState("study");
  const [selectedTaskType, setSelectedTaskType] = useState("tracks");
  const [studyData, setStudyData] = useState([]);
  const [labelersData, setLabelersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [studyPageNum, setStudyPageNum] = useState(1);
  const [labelersPageNum, setLabelersPageNum] = useState(1);
  const [studyDataCount, setStudyDataCount] = useState(0);
  const [labelersDataCount, setLabelersDataCount] = useState(0);

  const generateColumns = (permissionType) => {
    if (permissionType === "study") {
      const correctField = "correct_" + selectedTaskType;
      const incorrectField = "incorrect_" + selectedTaskType;

      return [
        {
          id: "name",
          accessorFn: (row) => `${row.user.first_name} ${row.user.last_name}`,
          header: "Name",
        },
        {
          id: "total",
          cell: (props) =>
            props.row.original[correctField] +
            props.row.original[incorrectField],
          header: "Total",
        },
        {
          accessorKey: "correct_" + selectedTaskType,
          header: "Correct",
        },
        {
          accessorKey: "incorrect_" + selectedTaskType,
          header: "Incorrect",
        },
        {
          id: "skill",
          cell: (props) => {
            const total =
              props.row.original[correctField] +
              props.row.original[incorrectField];
            return !total
              ? 0
              : ((props.row.original[correctField] / total) * 100)
                  .toFixed(2)
                  .replace(/[.,]00$/, "");
          },
          header: "Skill",
        },
        {
          id: "Actions",
          cell: (props) => {
            return (
              <div className="d-flex gap-2 float-end">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => {
                    addAccess(props.row.original.user.id, {
                      [getFieldName(selectedTaskType)]: true,
                    });
                  }}
                >
                  Allow Access
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const fullName =
                      props.row.original.user.first_name +
                      " " +
                      props.row.original.user.last_name;
                    const result = window.confirm(
                      `Are you sure you want remove access for ${fullName}?`
                    );

                    // Check the user's response
                    if (result) {
                      addAccess(props.row.original.user.id, {
                        [getFieldName(selectedTaskType)]: false,
                      });
                    }
                  }}
                >
                  Remove Access
                </Button>
                <Button
                  variant="secondary h-100"
                  size="sm"
                  onClick={() => reset(props.row.original.user.id)}
                >
                  <FontAwesomeIcon icon={["fas", "trash-can"]} /> Reset
                </Button>
              </div>
            );
          },
        },
      ];
    } else if (permissionType === "labeler") {
      return [
        {
          id: "name",
          accessorFn: (row) =>
            `${row.user_info.first_name} ${row.user_info.last_name}`,
          header: "Name",
        },
        {
          accessorFn: (row) => row.state["track"] || 0,
          header: "Tracks",
        },
        {
          id: "Actions",
          cell: (props) => {
            return (
              <div className="d-flex gap-2 float-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const fullName =
                      props.row.original.user_info.first_name +
                      " " +
                      props.row.original.user_info.last_name;
                    const result = window.confirm(
                      `Are you sure you want delete ${fullName}?`
                    );
                    if (result) deleteUser(props.row.original.user_info.id);
                  }}
                >
                  Delete User
                </Button>
              </div>
            );
          },
        },
      ];
    }
  };

  const columns = generateColumns(selectedPermissionType);

  const table = useReactTable({
    data: selectedPermissionType === "study" ? studyData : labelersData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchMoreStudy = async () => {
    setStudyPageNum(studyPageNum + 1);
    try {
      setIsLoadingMore(true);
      const { results } = await managerApi.getStudyFullReport(studyPageNum + 1);
      if (results) {
        setStudyData([...studyData, ...results]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
      toast.error(errorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const fetchMoreLabelers = async () => {
    setLabelersPageNum(labelersPageNum + 1);
    try {
      setIsLoadingMore(true);
      const { results } = await managerApi.getFullReport(labelersPageNum + 1);
      if (results) {
        setLabelersData([...labelersData, ...results]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
      toast.error(errorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    async function fetchStudyData() {
      try {
        setIsLoading(true);
        const { results, count } = await managerApi.getStudyFullReport();
        if (results && count) {
          setStudyData(results);
          setStudyDataCount(count);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 
          (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchLabelersData() {
      try {
        setIsLoading(true);
        const { results, count } = await managerApi.getFullReport();
        if (results) {
          setLabelersData(results);
          setLabelersDataCount(count);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 
          (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
    fetchStudyData();
    fetchLabelersData();
  }, []);

  const handleLoadMore = async () => {
    if (selectedPermissionType === "study") {
      await fetchMoreStudy();
    } else if (selectedPermissionType === "labeler") {
      await fetchMoreLabelers();
    }
  };

  const handleSelectPermissionType = (permission) => {
    setSelectedPermissionType(permission);
  };

  const handleSelectTaskType = (taskType) => {
    setSelectedTaskType(taskType);
  };

  const addAccess = async (userId, payload) => {
    try {
      await managerApi.updateUserPermissions(userId, payload);
      toast.success("Access has been successfully updated.");
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
      toast.error(errorMessage);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await managerApi.deleteUser(userId);
      const newLabelersData = labelersData.filter(
        ({ user_info }) => user_info.id !== userId
      );
      setLabelersData(newLabelersData);
      toast.success("User has been successfully deleted.");
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
      toast.error(errorMessage);
    }
  };

  const reset = async (userId) => {
    try {
      const result = window.confirm(`Are you sure you want reset study data?`);
      if (result) {
        await managerApi.resetStudyData(userId);
        const newStudyData = studyData.filter(({ user }) => user.id !== userId);
        setStudyData(newStudyData);
        toast.success("The data has been reset successfully.");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (JSON.stringify(err.response?.data)?.slice(0, 200) || "Something went wrong.");
      toast.error(errorMessage);
    }
  };

  return (
    <div className="tw-w-full tw-h-full tw-py-3 tw-px-4">
      <div className="tw-py-2">
        <Nav
          justify
          variant="tabs"
          defaultActiveKey="study"
          onSelect={handleSelectPermissionType}
        >
          {/* <Nav.Item>
            <Nav.Link eventKey="study">Study</Nav.Link>
          </Nav.Item> */}
          <Nav.Item>
            <Nav.Link eventKey="labeler">Labeler</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="moderators">Moderators</Nav.Link>
          </Nav.Item>
        </Nav>
      </div>
      {selectedPermissionType === "study" ? (
        <div className="tw-flex tw-justify-center tw-py-2">
          <div className="tw-rounded-full tw-bg-white tw-w-fit tw-px-2 tw-py-1 tw-shadow-sm">
            <Tabs
              tabs={internTaskOptions}
              onChange={(index) => {
                setSelectedTaskType(internTaskOptions[index].value);
              }}
            />
          </div>
        </div>
      ) : null}
      {selectedPermissionType === "labeler" ? (
        <div className="d-flex justify-content-center py-2">
          <div className="tw-rounded-full tw-bg-white tw-w-fit tw-px-2 tw-py-1 tw-shadow-sm">
            <Tabs tabs={timePeriods} onChange={() => {}} />
          </div>
        </div>
      ) : null}
      <Row className="tw-flex tw-justify-center">
        <Col xs={12} lg={8}>
          {(selectedPermissionType === "study" && studyData.length) ||
          (selectedPermissionType === "labeler" && labelersData.length) ? (
            <>
              <BTable className="manager-table" responsive hover size="md">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </BTable>
              {(selectedPermissionType === "study" &&
                studyData.length < studyDataCount) ||
              (selectedPermissionType === "labeler" &&
                labelersData.length < labelersDataCount) ? (
                <Button
                  className="w-100"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  Load more
                </Button>
              ) : null}
            </>
          ) : (
            <div className="m-3 d-flex justify-content-center">
              {isLoading ? (
                <Spinner animation="border" role="status" size="sm">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                "Sorry, no data is available at the moment."
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Manager;
